import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Button } from '@os/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@os/components/ui/badge';
import { LineChart, BarChart, PieChart, ScatterChart, Line, Bar, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { LineChart as LineChartIcon, BarChart3, PieChart as PieChartIcon, Activity } from 'lucide-react';
import * as ss from 'simple-statistics';

interface DataVisualizationProps {
  data: number[];
}

export const DataVisualization: React.FC<DataVisualizationProps> = ({ data }) => {
  const [chartType, setChartType] = useState('histogram');
  const [binCount, setBinCount] = useState('auto');

  // Calculate histogram data
  const histogramData = useMemo(() => {
    if (data.length === 0) return [];

    const minVal = Math.min(...data);
    const maxVal = Math.max(...data);
    const range = maxVal - minVal;
    
    // Calculate number of bins
    let numBins: number;
    if (binCount === 'auto') {
      // Sturges' rule
      numBins = Math.ceil(Math.log2(data.length) + 1);
    } else {
      numBins = parseInt(binCount);
    }

    const binWidth = range / numBins;
    const bins = Array(numBins).fill(0).map((_, i) => ({
      binStart: minVal + i * binWidth,
      binEnd: minVal + (i + 1) * binWidth,
      count: 0,
      frequency: 0,
      binCenter: minVal + (i + 0.5) * binWidth
    }));

    // Count values in each bin
    data.forEach(value => {
      const binIndex = Math.min(Math.floor((value - minVal) / binWidth), numBins - 1);
      bins[binIndex].count++;
    });

    // Calculate frequencies
    bins.forEach(bin => {
      bin.frequency = bin.count / data.length;
    });

    return bins.map((bin, i) => ({
      bin: `${bin.binStart.toFixed(2)}-${bin.binEnd.toFixed(2)}`,
      binNumber: i + 1,
      count: bin.count,
      frequency: bin.frequency,
      binCenter: bin.binCenter
    }));
  }, [data, binCount]);

  // Calculate box plot data
  const boxPlotData = useMemo(() => {
    if (data.length === 0) return null;

    const sorted = [...data].sort((a, b) => a - b);
    const q1 = ss.quantile(data, 0.25);
    const q3 = ss.quantile(data, 0.75);
    const iqr = q3 - q1;
    const median = ss.median(data);
    const min = Math.min(...data);
    const max = Math.max(...data);

    // Calculate outliers
    const lowerFence = q1 - 1.5 * iqr;
    const upperFence = q3 + 1.5 * iqr;
    const outliers = data.filter(x => x < lowerFence || x > upperFence);

    // Whiskers extend to the farthest non-outlier points
    const lowerWhisker = Math.min(...data.filter(x => x >= lowerFence));
    const upperWhisker = Math.max(...data.filter(x => x <= upperFence));

    return {
      min,
      max,
      q1,
      q3,
      median,
      lowerWhisker,
      upperWhisker,
      outliers,
      mean: ss.mean(data)
    };
  }, [data]);

  // Generate normal distribution overlay for histogram
  const normalOverlay = useMemo(() => {
    if (data.length === 0) return [];

    const mean = ss.mean(data);
    const std = ss.standardDeviation(data);
    const minVal = Math.min(...data);
    const maxVal = Math.max(...data);
    
    const points = [];
    const numPoints = 100;
    for (let i = 0; i <= numPoints; i++) {
      const x = minVal + (i / numPoints) * (maxVal - minVal);
      const y = (1 / (std * Math.sqrt(2 * Math.PI))) * 
                Math.exp(-0.5 * Math.pow((x - mean) / std, 2));
      // Scale to match histogram frequency scale
      const scaledY = y * (maxVal - minVal) / (binCount === 'auto' ? Math.ceil(Math.log2(data.length) + 1) : parseInt(binCount));
      points.push({ x, y: scaledY, binCenter: x });
    }
    
    return points;
  }, [data, binCount]);

  // Summary statistics
  const summaryStats = useMemo(() => {
    if (data.length === 0) return null;

    return {
      count: data.length,
      mean: ss.mean(data),
      median: ss.median(data),
      mode: ss.mode(data),
      standardDeviation: ss.standardDeviation(data),
      variance: ss.variance(data),
      skewness: ss.sampleSkewness(data),
      kurtosis: ss.sampleKurtosis(data),
      min: Math.min(...data),
      max: Math.max(...data),
      range: Math.max(...data) - Math.min(...data)
    };
  }, [data]);

  const chartOptions = [
    { value: 'histogram', label: 'Histogram', icon: BarChart3, description: 'Distribution of values' },
    { value: 'boxplot', label: 'Box Plot', icon: LineChartIcon, description: 'Five-number summary' },
    { value: 'density', label: 'Density Plot', icon: LineChartIcon, description: 'Smoothed distribution' },
    { value: 'summary', label: 'Summary Table', icon: PieChartIcon, description: 'Statistical summary' }
  ];

  const binOptions = [
    { value: 'auto', label: 'Auto (Sturges)' },
    { value: '5', label: '5 bins' },
    { value: '10', label: '10 bins' },
    { value: '15', label: '15 bins' },
    { value: '20', label: '20 bins' },
    { value: '30', label: '30 bins' }
  ];

  if (data.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Data Available</h3>
          <p className="text-muted-foreground">Enter numerical data to create visualizations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-cyan-500 text-white rounded-lg">
          <LineChartIcon size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Data Visualization</h2>
          <p className="text-muted-foreground">Visual analysis of your dataset</p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Visualization Controls</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Chart Type</label>
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {chartOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <option.icon size={16} />
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-muted-foreground">{option.description}</div>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(chartType === 'histogram' || chartType === 'density') && (
              <div>
                <label className="text-sm font-medium">Number of Bins</label>
                <Select value={binCount} onValueChange={setBinCount}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {binOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-end">
              <div className="text-sm">
                <div className="font-medium">Dataset Summary</div>
                <div className="text-muted-foreground">
                  {data.length} observations
                </div>
                <div className="text-muted-foreground">
                  Range: {Math.min(...data).toFixed(2)} to {Math.max(...data).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Visualization */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {chartOptions.find(opt => opt.value === chartType)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartType === 'histogram' && (
            <div className="space-y-4">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={histogramData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="bin" 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={10}
                    />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'count' ? `${value} observations` : `${(value as number * 100).toFixed(1)}%`,
                        name === 'count' ? 'Count' : 'Frequency'
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="count" fill="hsl(var(--primary))" name="count" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              <div className="text-sm text-muted-foreground">
                <p>• Bin width: {((Math.max(...data) - Math.min(...data)) / histogramData.length).toFixed(4)}</p>
                <p>• Total bins: {histogramData.length}</p>
                <p>• Most frequent bin: {histogramData.reduce((max, bin) => bin.count > max.count ? bin : max, histogramData[0])?.bin}</p>
              </div>
            </div>
          )}

          {chartType === 'density' && (
            <div className="space-y-4">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number"
                      dataKey="binCenter"
                      domain={['dataMin', 'dataMax']}
                    />
                    <YAxis />
                    <Tooltip 
                      formatter={(value) => [(value as number).toFixed(4), 'Frequency']}
                      labelFormatter={(value) => `Value: ${(value as number).toFixed(2)}`}
                    />
                    <Legend />
                    <Bar 
                      dataKey="frequency" 
                      fill="hsl(var(--primary))" 
                      opacity={0.6}
                      name="Observed"
                    />
                    <Line 
                      data={normalOverlay}
                      type="monotone" 
                      dataKey="y" 
                      stroke="hsl(var(--destructive))" 
                      strokeWidth={2}
                      dot={false}
                      name="Normal Overlay"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              <div className="text-sm text-muted-foreground">
                <p>• Red line shows normal distribution overlay with same mean and standard deviation</p>
                <p>• Bars show the actual frequency distribution of your data</p>
              </div>
            </div>
          )}

          {chartType === 'boxplot' && boxPlotData && (
            <div className="space-y-4">
              <div className="h-80 flex items-center justify-center">
                <div className="w-full max-w-md">
                  {/* Custom box plot visualization */}
                  <div className="relative bg-muted p-8 rounded-lg">
                    <div className="text-center mb-4 font-medium">Box Plot</div>
                    
                    {/* Value scale */}
                    <div className="flex justify-between text-xs text-muted-foreground mb-2">
                      <span>{boxPlotData.min.toFixed(2)}</span>
                      <span>{boxPlotData.max.toFixed(2)}</span>
                    </div>
                    
                    {/* Box plot elements */}
                    <div className="relative h-16 border-l-2 border-r-2 border-foreground/20">
                      {/* Whiskers */}
                      <div 
                        className="absolute top-6 bottom-6 w-px bg-foreground"
                        style={{ 
                          left: `${((boxPlotData.lowerWhisker - boxPlotData.min) / (boxPlotData.max - boxPlotData.min)) * 100}%` 
                        }}
                      />
                      <div 
                        className="absolute top-6 bottom-6 w-px bg-foreground"
                        style={{ 
                          left: `${((boxPlotData.upperWhisker - boxPlotData.min) / (boxPlotData.max - boxPlotData.min)) * 100}%` 
                        }}
                      />
                      
                      {/* Box */}
                      <div 
                        className="absolute top-3 bottom-3 border-2 border-primary bg-primary/20"
                        style={{
                          left: `${((boxPlotData.q1 - boxPlotData.min) / (boxPlotData.max - boxPlotData.min)) * 100}%`,
                          width: `${((boxPlotData.q3 - boxPlotData.q1) / (boxPlotData.max - boxPlotData.min)) * 100}%`
                        }}
                      />
                      
                      {/* Median line */}
                      <div 
                        className="absolute top-3 bottom-3 w-0.5 bg-foreground"
                        style={{ 
                          left: `${((boxPlotData.median - boxPlotData.min) / (boxPlotData.max - boxPlotData.min)) * 100}%` 
                        }}
                      />
                      
                      {/* Mean marker */}
                      <div 
                        className="absolute top-1/2 transform -translate-y-1/2 w-2 h-2 bg-destructive rounded-full"
                        style={{ 
                          left: `${((boxPlotData.mean - boxPlotData.min) / (boxPlotData.max - boxPlotData.min)) * 100}%` 
                        }}
                      />
                    </div>
                    
                    {/* Legend */}
                    <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-0.5 bg-foreground"></div>
                        <span>Median</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-destructive rounded-full"></div>
                        <span>Mean</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 border border-primary"></div>
                        <span>IQR</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-px bg-foreground"></div>
                        <span>Whiskers</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div>
                  <div className="font-medium">Minimum</div>
                  <div className="text-muted-foreground">{boxPlotData.min.toFixed(4)}</div>
                </div>
                <div>
                  <div className="font-medium">Q1</div>
                  <div className="text-muted-foreground">{boxPlotData.q1.toFixed(4)}</div>
                </div>
                <div>
                  <div className="font-medium">Median</div>
                  <div className="text-muted-foreground">{boxPlotData.median.toFixed(4)}</div>
                </div>
                <div>
                  <div className="font-medium">Q3</div>
                  <div className="text-muted-foreground">{boxPlotData.q3.toFixed(4)}</div>
                </div>
                <div>
                  <div className="font-medium">Maximum</div>
                  <div className="text-muted-foreground">{boxPlotData.max.toFixed(4)}</div>
                </div>
              </div>
              
              {boxPlotData.outliers.length > 0 && (
                <div className="text-sm">
                  <div className="font-medium mb-1">Outliers ({boxPlotData.outliers.length}):</div>
                  <div className="text-muted-foreground">
                    {boxPlotData.outliers.slice(0, 10).map(o => o.toFixed(2)).join(', ')}
                    {boxPlotData.outliers.length > 10 && '...'}
                  </div>
                </div>
              )}
            </div>
          )}

          {chartType === 'summary' && summaryStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-3">
                <h4 className="font-medium">Central Tendency</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Mean:</span>
                    <span className="font-mono">{summaryStats.mean.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Median:</span>
                    <span className="font-mono">{summaryStats.median.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Mode:</span>
                    <span className="font-mono">
                      {Array.isArray(summaryStats.mode) 
                        ? summaryStats.mode.join(', ') 
                        : summaryStats.mode?.toFixed(4) || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium">Variability</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Std Deviation:</span>
                    <span className="font-mono">{summaryStats.standardDeviation.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Variance:</span>
                    <span className="font-mono">{summaryStats.variance.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Range:</span>
                    <span className="font-mono">{summaryStats.range.toFixed(4)}</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium">Shape & Position</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Skewness:</span>
                    <span className="font-mono">{summaryStats.skewness.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Kurtosis:</span>
                    <span className="font-mono">{summaryStats.kurtosis.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Count:</span>
                    <span className="font-mono">{summaryStats.count}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};